import {
  FOOTPRINT_DEFAULT_STYLE,
  FOOTPRINT_FACTION_STYLES,
  FOOTPRINT_GAITS,
  FOOTPRINT_HEAT_TINTS,
  FOOTPRINT_SHEET,
  FOOTPRINT_TIERS,
  FOOTPRINT_WALKER_STEPS,
  UNIT_FOOTPRINTS,
} from '../../configs/unitFootprintsAnimation';
import {UNIT_TYPES} from '../../configs/units';

/**
 * ОПИСАНИЕ СЛЕДА ЗА ОТРЯДОМ: чистая функция от пути и отряда, без сцены. Каждая деталь
 * следа знает, на каком месте пути она проступает (atS): она появляется, когда отряд
 * дошел до этого места. Часы следа — сам отряд, а не таймер, поэтому след не обгоняет
 * его при неровных кадрах и сжимается вместе с ним (см. scenes/UnitFootprints).
 *
 * Детали трех видов. Лежащие на земле (подпалина, свечение, отпечаток, трещина, полоса)
 * ждут прихода отряда и остывают от начала пути к концу. Короткие (вспышка, искра, пыль,
 * волна, лучи и частицы фракции) живут своим сроком с момента, как проступили. Свет отряда
 * (аура, его лучи) идет вместе с ним и гаснет на приходе.
 *
 * Частица фракции движется так, как движется ее материал (UNIT_FOOTPRINTS.particles):
 * физика следует из того, что это за предмет.
 */

const STAY_KINDS = new Set(['scorch', 'burn', 'print', 'crack', 'ribbon']);
const FOLLOW_KINDS = new Set(['aura', 'auraRays']);
// Тяжелое и брызги: летят дугой на землю
const THROWN_KINDS = new Set(['debris', 'coin', 'bone', 'drop']);

const frameOf = (name) => FOOTPRINT_SHEET.frames.indexOf(name);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Длины пути от начала до каждой вершины */
export const buildPathDistances = (points) => {
  const distances = [0];
  for (let index = 1; index < points.length; index += 1) {
    const last = distances[index - 1];
    distances.push(last + Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y));
  }
  return distances;
};

const pointAt = (points, distances, s) => {
  const target = clamp(s, 0, distances[distances.length - 1]);
  let index = 1;
  while (index < distances.length - 1 && distances[index] < target) {
    index += 1;
  }
  const length = distances[index] - distances[index - 1];
  const share = length > 0 ? (target - distances[index - 1]) / length : 0;
  return {
    x: points[index - 1].x + (points[index].x - points[index - 1].x) * share,
    y: points[index - 1].y + (points[index].y - points[index - 1].y) * share,
  };
};

/**
 * Докуда отряд дошел по пути: ближайшая к нему точка пути не раньше уже пройденной.
 * Пройденное место не откатывается, а за низкий кадр отряд может проскочить целый
 * отрезок: поэтому ищем по всем оставшимся
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {number[]} distances
 * @param {number} walked — пройдено к прошлому кадру
 * @param {{x: number, y: number}} position — где отряд сейчас
 * @returns {number}
 */
export const resolveWalkedDistance = (points, distances, walked, position) => {
  let best = walked;
  let bestDistance = Infinity;
  for (let index = 1; index < points.length; index += 1) {
    if (distances[index] >= walked) {
      const from = points[index - 1];
      const dx = points[index].x - from.x;
      const dy = points[index].y - from.y;
      const lengthSquared = dx * dx + dy * dy;
      const share = lengthSquared > 0
        ? clamp(((position.x - from.x) * dx + (position.y - from.y) * dy) / lengthSquared, 0, 1)
        : 0;
      const distance = Math.hypot(position.x - from.x - dx * share, position.y - from.y - dy * share);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = Math.max(walked, distances[index - 1] + (distances[index] - distances[index - 1]) * share);
      }
    }
  }
  return best;
};

/** Сколько идут рядом при такой численности */
export const resolveWalkerCount = (unitSize, {isSingleBody = false} = {}) => {
  if (isSingleBody) {
    return 1;
  }
  return FOOTPRINT_WALKER_STEPS.find((step) => unitSize >= step.minSize)?.count ?? 1;
};

/**
 * Вид следа отряда: только отряд, нарисованный обликом фракции, оставляет ее следы
 *
 * @param {string} unitTypeId
 * @param {string|null} factionId
 * @param {boolean} hasFactionSkin
 * @returns {{tier: Object, printFrames: string[], motif: Object|null, hasTail: boolean, isSingleBody: boolean}}
 */
export const resolveFootprintStyle = (unitTypeId, factionId, hasFactionSkin) => {
  const tier = FOOTPRINT_TIERS[unitTypeId] ?? FOOTPRINT_TIERS[UNIT_TYPES.D6.id];
  const style = (hasFactionSkin && FOOTPRINT_FACTION_STYLES[factionId]) || FOOTPRINT_DEFAULT_STYLE;
  let prints;
  if (tier.gait === FOOTPRINT_GAITS.WHEELS) {
    prints = 'rut';
  } else if (unitTypeId === UNIT_TYPES.D12.id) {
    prints = style.heavyMount ?? style.mount;
  } else if (unitTypeId === UNIT_TYPES.D8.id) {
    prints = style.mount;
  } else if (unitTypeId === UNIT_TYPES.D20.id) {
    prints = style.hero ?? style.foot;
  } else {
    prints = style.foot;
  }
  const motifs = [].concat(style.motif ?? []);
  return {
    tier,
    printFrames: Array.isArray(prints) ? prints : [prints],
    motif: motifs[0] ?? null,
    motifs,
    hasTail: Boolean(style.tail) && tier.gait !== FOOTPRINT_GAITS.WHEELS,
    isSingleBody: Boolean(style.isSingleBody),
    isViolent: Boolean(style.isViolent),
    isEarthshaker: Boolean(style.isEarthshaker),
    printScale: unitTypeId === UNIT_TYPES.D20.id ? (style.heroPrintScale ?? 1) : 1,
  };
};

/**
 * Дрожь камеры от шага отряда такой численности: с порога чуть заметная, дальше сильнее
 *
 * @param {number} unitSize
 * @param {number} [scale] — множитель силы: удар на приходе тяжелее шага
 * @returns {{durationMs: number, intensity: number}|null} null — отряд для этого мал
 */
export const resolveQuakeShake = (unitSize, scale = 1) => {
  const {fromSize, fullSize, minIntensity, maxIntensity, minDurationMs, maxDurationMs} = UNIT_FOOTPRINTS.quake.camera;
  if (!(unitSize >= fromSize)) {
    return null;
  }
  const share = clamp((unitSize - fromSize) / (fullSize - fromSize), 0, 1);
  return {
    durationMs: minDurationMs + (maxDurationMs - minDurationMs) * share,
    intensity: (minIntensity + (maxIntensity - minIntensity) * share) * scale,
  };
};

/** Цвет раскаленного металла по накалу 0..1: мелкими шагами, см. FOOTPRINT_HEAT_TINTS */
export const resolveHeatTint = (heat) => (
  FOOTPRINT_HEAT_TINTS[Math.round(clamp(heat, 0, 1) * (FOOTPRINT_HEAT_TINTS.length - 1))]
);

const pickSparkTint = (rng, isViolent) => {
  const {gold, paleGold, amber} = UNIT_FOOTPRINTS.colors;
  const tints = isViolent ? UNIT_FOOTPRINTS.violentSparkTints : [gold, paleGold, amber, gold];
  return tints[Math.floor(rng() * tints.length) % tints.length];
};

const buildSpark = ({x, y, atS, hexSize, rng, reachFactor, isViolent = false}) => {
  const {lifeMs, lifeSpread, sizeFactor, sizeSpread, riseFactor, gravityFactor} = UNIT_FOOTPRINTS.spark;
  const angle = rng() * Math.PI * 2;
  const reach = reachFactor * hexSize * (0.45 + rng() * 0.55);
  const driftX = Math.cos(angle) * reach;
  const driftY = Math.sin(angle) * reach * 0.6;
  const rise = riseFactor * hexSize * (0.5 + rng() * 0.7);
  return {
    kind: 'spark', frame: frameOf('streak'), atS, x, y,
    size: sizeFactor * hexSize * (1 - sizeSpread / 2 + rng() * sizeSpread),
    alpha: 1,
    tint: pickSparkTint(rng, isViolent),
    rotation: Math.atan2(driftY - rise, driftX) + Math.PI / 2,
    turn: 0,
    driftX,
    driftY,
    rise,
    fall: gravityFactor * hexSize,
    lifeMs: lifeMs * (1 - lifeSpread / 2 + rng() * lifeSpread),
  };
};

// Общее у всех частиц фракции: кадр, цвет, размер и срок по настройкам материала
const buildParticleBase = ({motif, settings, atS, x, y, hexSize, rng}) => ({
  kind: motif.motion,
  frame: frameOf(motif.frame),
  atS,
  x,
  y,
  size: settings.sizeFactor * hexSize * (1 - settings.sizeSpread / 2 + rng() * settings.sizeSpread) * (motif.sizeScale ?? 1),
  alpha: settings.alpha ?? 1,
  tint: motif.tint,
  lifeMs: settings.lifeMs * (1 - settings.lifeSpread / 2 + rng() * settings.lifeSpread),
});

/**
 * Частицы одного выброса материала motif. direction — куда идет отряд: тяжелое летит назад
 * от него; null — всплеск на старте или на приходе. Брызги оставляют на земле круг
 *
 * @returns {Array} детали следа
 */
const buildParticle = ({motif, x, y, atS, hexSize, rng, direction = null, delayMs = 0}) => {
  const {particles, ring} = UNIT_FOOTPRINTS;
  const settings = particles[motif.motion];
  const base = buildParticleBase({motif, settings, atS, x, y, hexSize, rng});
  const isBurst = direction === null;

  if (THROWN_KINDS.has(motif.motion)) {
    const angle = isBurst ? rng() * Math.PI * 2 : direction + Math.PI + (rng() * 2 - 1) * settings.coneRadians;
    const reach = (isBurst ? settings.burstReachFactor : settings.reachFactor) * hexSize * (0.5 + rng() * 0.5);
    const thrown = {
      ...base,
      rotation: rng() * Math.PI * 2,
      turn: (rng() * 2 - 1) * settings.spin,
      driftX: Math.cos(angle) * reach,
      driftY: Math.sin(angle) * reach * 0.7,
      lift: settings.liftFactor * hexSize * (0.5 + rng() * 0.7),
    };
    const items = [thrown];
    if (settings.ripple && rng() < settings.ripple.chance) {
      items.push({
        kind: 'ring', frame: frameOf('ring'), atS, x: x + thrown.driftX, y: y + thrown.driftY,
        size: settings.ripple.sizeFactor * hexSize, alpha: settings.ripple.alpha, tint: motif.tint, rotation: 0,
        delayMs: thrown.lifeMs * settings.landShare, lifeMs: settings.ripple.lifeMs,
      });
    }
    return items;
  }

  // Легкое возникает рядом с шагом, а на всплеске — по всей ячейке
  const scatter = (isBurst ? particles.burstScatterFactor : settings.spreadFactor) * hexSize;
  const offsetX = (rng() * 2 - 1) * scatter;
  const offsetY = (rng() * 2 - 1) * scatter * 0.7;
  const placed = {...base, x: x + offsetX, y: y + offsetY, delayMs};
  if (motif.motion === 'flame') {
    return [{...placed, rotation: (rng() * 2 - 1) * 0.16, rise: settings.riseFactor * hexSize * (0.7 + rng() * 0.6)}];
  }
  if (motif.motion === 'flutter') {
    return [{
      ...placed,
      rotation: rng() * Math.PI * 2,
      driftX: (rng() * 2 - 1) * settings.driftFactor * hexSize,
      lift: settings.liftFactor * hexSize * (0.6 + rng() * 0.8),
      fall: settings.fallFactor * hexSize,
      sway: settings.swayFactor * hexSize,
      swayTurns: settings.swayTurns,
      swayPhase: rng() * Math.PI * 2,
      rock: settings.rock,
    }];
  }
  if (motif.motion === 'puff') {
    const angle = rng() * Math.PI * 2;
    return [{
      ...placed,
      rotation: rng() * Math.PI * 2,
      driftX: Math.cos(angle) * settings.driftFactor * hexSize,
      driftY: Math.sin(angle) * settings.driftFactor * hexSize * 0.7,
      rise: settings.riseFactor * hexSize,
      turn: (rng() * 2 - 1) * settings.turn,
    }];
  }
  // Всплывает: держится прямо, только чуть покачивается
  return [{
    ...placed,
    rotation: 0,
    rise: settings.riseFactor * hexSize * (0.7 + rng() * 0.6),
    wobble: settings.wobbleFactor * hexSize,
    wobblePhase: rng() * Math.PI * 2,
  }];
};

const buildBurst = ({items, burst, center, atS, hexSize, motifs, isViolent, isEarthshaker = false, rng, isArrival}) => {
  if (!burst) {
    return;
  }
  const {colors, spark, dust, particles} = UNIT_FOOTPRINTS;
  const pickMotif = () => motifs[Math.floor(rng() * motifs.length) % motifs.length];
  const {
    spreadFactor, dustSizeFactor, dustAlpha, flashSizeFactor, flashLifeMs, flashAlpha, raysSizeFactor, raysLifeMs,
    raysAlpha, hexRingToFactor, hexRingDelaysMs, hexRingLifeMs, crackSizeFactor, crackAlpha,
  } = UNIT_FOOTPRINTS.burst;
  const spread = () => (rng() * 2 - 1) * spreadFactor * hexSize;

  if (burst.crack && isEarthshaker) {
    // Удар великана о землю: такой же тонкий темный разлом, как от его шагов, только шире
    items.push({
      kind: 'crack', frame: frameOf('fissure'), atS, x: center.x, y: center.y,
      size: crackSizeFactor * hexSize, alpha: UNIT_FOOTPRINTS.quake.crackAlpha, tint: UNIT_FOOTPRINTS.crack.tint, rotation: rng() * Math.PI * 2,
    });
  } else if (burst.crack) {
    const rotation = rng() * Math.PI * 2;
    items.push(
      {
        kind: 'crack', frame: frameOf('crack'), atS, x: center.x, y: center.y,
        size: crackSizeFactor * hexSize, alpha: crackAlpha, tint: UNIT_FOOTPRINTS.crack.tint, rotation,
      },
      {
        kind: 'crack', frame: frameOf('crack'), atS, x: center.x, y: center.y, isAdd: true,
        size: crackSizeFactor * hexSize * 0.98, alpha: crackAlpha * 0.9, tint: UNIT_FOOTPRINTS.crack.glowTint, rotation,
      }
    );
  }
  if (burst.flashFactor > 0) {
    items.push({
      kind: 'flash', frame: frameOf('glow'), atS, x: center.x, y: center.y, isAdd: true,
      size: flashSizeFactor * hexSize * burst.flashFactor, alpha: flashAlpha, tint: colors.gold, rotation: 0, lifeMs: flashLifeMs,
    });
  }
  if (burst.rays) {
    items.push({
      kind: 'flash', frame: frameOf('rays'), atS, x: center.x, y: center.y, isAdd: true,
      size: raysSizeFactor * hexSize * (isArrival ? 1 : 0.8), alpha: raysAlpha, tint: colors.paleGold,
      rotation: rng() * Math.PI, turn: 0.5, lifeMs: raysLifeMs,
    });
  }
  if (burst.hexRing) {
    hexRingDelaysMs.forEach((delayMs, index) => {
      items.push({
        kind: 'ring', frame: frameOf('hex_ring'), atS, x: center.x, y: center.y, isAdd: index === 0,
        size: hexRingToFactor * hexSize * (isArrival ? 1 : 0.8), alpha: UNIT_FOOTPRINTS.ring.alpha,
        tint: index === 0 ? colors.gold : colors.amber, rotation: 0, delayMs, lifeMs: hexRingLifeMs,
      });
    });
  }
  for (let index = 0; index < burst.dustCount; index += 1) {
    items.push({
      kind: 'dust', frame: frameOf('dust'), atS, x: center.x + spread(), y: center.y + spread() * 0.6,
      size: dustSizeFactor * hexSize * (0.75 + rng() * 0.5), alpha: dustAlpha, tint: dust.tint,
      driftX: (rng() * 2 - 1) * dust.driftFactor * hexSize, rotation: rng() * Math.PI * 2,
    });
  }
  for (let index = 0; index < burst.sparkCount; index += 1) {
    items.push(buildSpark({x: center.x, y: center.y, atS, hexSize, rng, reachFactor: spark.burstReachFactor, isViolent}));
  }
  // Сколько частиц материала в этом всплеске: у тяжелого больше, у легкого меньше
  if (motifs.length > 0) {
    const count = Math.round(burst.motifCount * particles[motifs[0].motion].burstShare);
    for (let index = 0; index < count; index += 1) {
      items.push(...buildParticle({
        motif: pickMotif(), x: center.x, y: center.y, atS, hexSize, rng, delayMs: rng() * particles.burstDelayMs,
      }));
    }
  }
};

/**
 * @param {Object} params
 * @param {Array<{x: number, y: number}>} params.points — путь по вершинам, от старта до цели
 * @param {number} params.hexSize
 * @param {string} params.unitTypeId
 * @param {number} params.unitSize
 * @param {string|null} params.factionId
 * @param {boolean} params.hasFactionSkin
 * @param {function(): number} [params.rng] — источник случайности (только вид следа)
 * @returns {{points: Array, distances: number[], length: number, holdMs: number, items: Array}|null}
 */
export const buildUnitFootprints = ({
  points, hexSize, unitTypeId, unitSize, factionId, hasFactionSkin, rng = Math.random,
}) => {
  if (!points || points.length < 2 || !(hexSize > 0)) {
    return null;
  }
  const distances = buildPathDistances(points);
  const length = distances[distances.length - 1];
  if (!(length > 0)) {
    return null;
  }

  const {
    tier, printFrames, motifs, hasTail, isSingleBody, isViolent, isEarthshaker, printScale,
  } = resolveFootprintStyle(unitTypeId, factionId, hasFactionSkin);
  const pickMotif = () => motifs[Math.floor(rng() * motifs.length) % motifs.length];
  const {
    colors, scorch, burn, spark, dust, ribbon, crack, particles, gouge,
    startInsetFactor, endInsetFactor, laneGapFactor, laneStrideGrow, laneJitterFactor, strideJitter, angleJitter,
    tangentReachFactor, laneStaggerShare, hindLagShare, hindGapShare, loneMountGapScale, tailEvery, singleBodyGrow,
    maxPrintsPerTrail, maxSparksPerTrail,
  } = UNIT_FOOTPRINTS;
  const isWheels = tier.gait === FOOTPRINT_GAITS.WHEELS;
  const laneCount = isWheels ? 1 : resolveWalkerCount(unitSize, {isSingleBody});
  const sizeGrow = isSingleBody ? Math.min(singleBodyGrow.max, 1 + unitSize * singleBodyGrow.perSize) : 1;
  const printSize = tier.printSizeFactor * hexSize * sizeGrow * printScale;
  const isQuadruped = tier.gait === FOOTPRINT_GAITS.QUADRUPED;
  // Один кавалерист не должен вытягиваться в цепочку по оси хода
  const footGap = tier.footGapFactor * hexSize * (isQuadruped && laneCount === 1 ? loneMountGapScale : 1);

  const from = startInsetFactor * hexSize;
  const to = length - endInsetFactor * hexSize;
  let stride = tier.strideFactor * hexSize * (isSingleBody ? sizeGrow : 1) * (1 + (laneCount - 1) * laneStrideGrow);
  // На колесо и на каждого идущего по цепочке отпечатков; длинный путь крупного отряда
  // прореживается шагом, а не обрывается. У четвероногих на шаг приходится пара копыт
  const chains = isWheels ? 2 : laneCount;
  const printsPerStep = tier.gait === FOOTPRINT_GAITS.QUADRUPED ? 2 : 1;
  const estimatedPrints = chains * printsPerStep * (to - from) / stride;
  if (estimatedPrints > maxPrintsPerTrail) {
    stride *= estimatedPrints / maxPrintsPerTrail;
  }

  const items = [];
  // Счетчик отпечатков на все ряды: по нему расходятся искры, пыль, знаки и трещины
  let printIndex = 0;
  let sparkCount = 0;
  const directionAt = (s) => {
    const reach = tangentReachFactor * hexSize;
    const before = pointAt(points, distances, s - reach);
    const after = pointAt(points, distances, s + reach);
    return Math.atan2(after.y - before.y, after.x - before.x);
  };
  // Середина между точкой шага и ее соседями: излом пути становится дугой
  const smoothedPointAt = (s) => {
    const reach = tangentReachFactor * hexSize;
    const before = pointAt(points, distances, s - reach);
    const here = pointAt(points, distances, s);
    const after = pointAt(points, distances, s + reach);
    return {x: (before.x + here.x * 2 + after.x) / 4, y: (before.y + here.y * 2 + after.y) / 4};
  };

  // Отпечаток: подпалина, свечение и сам раскаленный рисунок, в этом порядке снизу вверх
  const pushPrint = ({frameName, atS, x, y, size, rotation, flipX, stretch = 1, alphaShare = 1, hasGlowLayers = true}) => {
    if (hasGlowLayers) {
      const glowFrame = frameOf(`${frameName}_glow`);
      const shared = {atS, x, y, rotation, flipX, stretch};
      items.push(
        {...shared, kind: 'scorch', frame: glowFrame, size: size * scorch.sizeFactor, alpha: scorch.alpha * alphaShare, tint: colors.scorch},
        {...shared, kind: 'burn', frame: glowFrame, size: size * burn.sizeFactor, alpha: tier.burnAlpha * alphaShare, tint: colors.amber}
      );
    }
    items.push({
      kind: 'print', frame: frameOf(frameName), atS, x, y, size, rotation, flipX, stretch,
      alpha: alphaShare, restHeat: tier.restHeat, tint: resolveHeatTint(1),
    });
  };

  for (let lane = 0; lane < chains; lane += 1) {
    const framesInLane = printFrames[Math.floor(rng() * printFrames.length) % printFrames.length];
    const laneOffset = isWheels
      ? (lane === 0 ? -1 : 1) * tier.footGapFactor * hexSize
      : (lane - (laneCount - 1) / 2) * laneGapFactor * hexSize;
    const laneStart = isWheels ? 0 : ((lane * laneStaggerShare) % 1) * stride;
    let s = from + laneStart;
    let step = 0;

    while (s <= to) {
      const direction = directionAt(s);
      const normal = {x: -Math.sin(direction), y: Math.cos(direction)};
      const center = smoothedPointAt(s);
      const side = isWheels ? 0 : (step % 2 === 0 ? -1 : 1);
      const lateral = laneOffset + side * footGap + (rng() * 2 - 1) * laneJitterFactor * hexSize;
      const x = center.x + normal.x * lateral;
      const y = center.y + normal.y * lateral;
      // Рисунок смотрит вверх, а 0 у направления — вправо
      const baseRotation = direction + Math.PI / 2;

      if (isWheels) {
        // Куски колеи ложатся встык: длину куска задает растяжение кадра
        pushPrint({
          frameName: framesInLane, atS: s, x, y, size: printSize, rotation: baseRotation, flipX: false,
          stretch: (stride * 1.08) / printSize, hasGlowLayers: step % 2 === 0,
        });
      } else {
        pushPrint({
          frameName: framesInLane, atS: s, x, y, size: printSize,
          rotation: baseRotation + (rng() * 2 - 1) * angleJitter, flipX: side > 0,
        });
      }

      if (isQuadruped) {
        // Заднее копыто ложится сзади и с другой стороны от линии хода: след идет елочкой
        const hindS = s - stride * hindLagShare;
        if (hindS >= from) {
          const hindCenter = smoothedPointAt(hindS);
          const hindLateral = laneOffset - side * footGap * hindGapShare;
          pushPrint({
            frameName: framesInLane, atS: hindS,
            x: hindCenter.x + normal.x * hindLateral, y: hindCenter.y + normal.y * hindLateral, size: printSize * 0.94,
            rotation: baseRotation + (rng() * 2 - 1) * angleJitter, flipX: side < 0, alphaShare: 0.92,
          });
        }
      }

      if (hasTail && step % tailEvery === 1) {
        pushPrint({
          frameName: 'tail', atS: s, x: center.x + normal.x * laneOffset, y: center.y + normal.y * laneOffset,
          size: printSize * 1.15, rotation: baseRotation, flipX: rng() < 0.5, alphaShare: 0.8, hasGlowLayers: false,
        });
      }

      if (isEarthshaker) {
        // Шаг, от которого дрожит земля: разлом под ступней, волна по поверхности, низкая
        // пыль во все стороны. Вверх ничего не летит
        const {quake} = UNIT_FOOTPRINTS;
        const crackRotation = rng() * Math.PI * 2;
        const crackSize = quake.crackSizeFactor * hexSize * sizeGrow;
        items.push(
          {kind: 'crack', frame: frameOf('fissure'), atS: s, x, y, size: crackSize, alpha: quake.crackAlpha, tint: crack.tint, rotation: crackRotation},
          {
            kind: 'ring', frame: frameOf('ring'), atS: s, x, y, size: quake.waveSizeFactor * hexSize * sizeGrow, stretch: quake.waveSquash,
            alpha: quake.waveAlpha, tint: quake.waveTint, rotation: 0, lifeMs: quake.waveLifeMs,
          }
        );
        for (let puff = 0; puff < quake.skirtCount; puff += 1) {
          const puffAngle = (puff / quake.skirtCount) * Math.PI * 2 + rng();
          const reach = quake.skirtReachFactor * hexSize * (0.7 + rng() * 0.5);
          items.push({
            kind: 'dust', frame: frameOf('dust'), atS: s, x, y, size: quake.skirtSizeFactor * hexSize * (0.75 + rng() * 0.5),
            alpha: quake.skirtAlpha, tint: dust.tint, rotation: rng() * Math.PI * 2, isLow: true,
            driftX: Math.cos(puffAngle) * reach, driftY: Math.sin(puffAngle) * reach * quake.waveSquash,
          });
        }
        const stompShake = resolveQuakeShake(unitSize);
        if (stompShake) {
          items.push({kind: 'quake', atS: s, ...stompShake});
        }
      }
      if (tier.crack && !isEarthshaker && printIndex % tier.crack.every === 0) {
        const crackRotation = rng() * Math.PI * 2;
        items.push(
          {kind: 'crack', frame: frameOf('crack'), atS: s, x, y, size: tier.crack.sizeFactor * hexSize, alpha: tier.crack.alpha, tint: crack.tint, rotation: crackRotation},
          {kind: 'crack', frame: frameOf('crack'), atS: s, x, y, isAdd: true, size: tier.crack.sizeFactor * hexSize * 0.98, alpha: tier.crack.alpha * 0.85, tint: crack.glowTint, rotation: crackRotation}
        );
      }
      if (tier.flash && printIndex % tier.flash.every === 0) {
        items.push({
          kind: 'flash', frame: frameOf('glow'), atS: s, x, y, isAdd: true, size: tier.flash.sizeFactor * hexSize,
          alpha: tier.flash.alpha, tint: colors.gold, rotation: 0, lifeMs: UNIT_FOOTPRINTS.flash.lifeMs,
        });
      }
      if (tier.dust && !isEarthshaker && printIndex % tier.dust.every === 0) {
        items.push({
          kind: 'dust', frame: frameOf('dust'), atS: s, x, y, size: tier.dust.sizeFactor * hexSize * (0.8 + rng() * 0.4),
          alpha: tier.dust.alpha, tint: dust.tint, driftX: (rng() * 2 - 1) * dust.driftFactor * hexSize, rotation: rng() * Math.PI * 2,
        });
      }
      if (tier.ring && printIndex % tier.ring.every === 0) {
        items.push({
          kind: 'ring', frame: frameOf('ring'), atS: s, x, y, size: tier.ring.toFactor * hexSize, isAdd: true,
          alpha: UNIT_FOOTPRINTS.ring.alpha, tint: colors.gold, rotation: 0, lifeMs: UNIT_FOOTPRINTS.ring.lifeMs,
        });
      }
      if (tier.sparks && printIndex % tier.sparks.every === 0) {
        for (let count = 0; count < tier.sparks.count && sparkCount < maxSparksPerTrail; count += 1) {
          items.push(buildSpark({x, y, atS: s, hexSize, rng, reachFactor: spark.reachFactor, isViolent}));
          sparkCount += 1;
        }
      }
      if (motifs.length > 0 && tier.motif && printIndex % tier.motif.every === 0) {
        for (let count = 0; count < tier.motif.count; count += 1) {
          const motif = pickMotif();
          const pieces = particles[motif.motion].stepShare;
          for (let piece = 0; piece < pieces; piece += 1) {
            items.push(...buildParticle({motif, x, y, atS: s, hexSize, rng, direction, delayMs: (count + piece) * 30}));
          }
        }
        if (isViolent && printIndex % (tier.motif.every * gouge.every) === 0) {
          // Борозды когтей: вдоль хода, горят в начале и остывают вместе со следом
          const gougeRotation = baseRotation + (rng() * 2 - 1) * 0.4;
          const gougeSize = gouge.sizeFactor * hexSize;
          items.push(
            {kind: 'crack', frame: frameOf('gouge'), atS: s, x, y, size: gougeSize, alpha: gouge.alpha, tint: crack.tint, rotation: gougeRotation},
            {kind: 'crack', frame: frameOf('gouge'), atS: s, x, y, isAdd: true, size: gougeSize * 0.96, alpha: gouge.alpha * 0.9, tint: crack.glowTint, rotation: gougeRotation}
          );
        }
      }

      printIndex += 1;
      step += 1;
      s += stride * (isWheels ? 1 : 1 + (rng() * 2 - 1) * strideJitter);
    }
  }

  if (tier.ribbon) {
    const ribbonStep = tier.ribbon.stepFactor * hexSize;
    for (let s = from; s <= length; s += ribbonStep) {
      const center = smoothedPointAt(s);
      items.push(
        {
          kind: 'ribbon', frame: frameOf('glow'), atS: s, x: center.x, y: center.y,
          size: tier.ribbon.widthFactor * hexSize * 1.5, alpha: tier.ribbon.alpha, tint: colors.amber, rotation: 0,
        },
        // Ядро узкое: шаг полосы ему велик, поэтому оно вытянуто вдоль пути и не идет бусами
        {
          kind: 'ribbon', frame: frameOf('glow'), atS: s, x: center.x, y: center.y, isAdd: true,
          size: tier.ribbon.widthFactor * hexSize * ribbon.coreShare * 1.5, alpha: tier.ribbon.coreAlpha,
          stretch: tier.ribbon.coreStretch, tint: colors.paleGold, rotation: directionAt(s) + Math.PI / 2,
        }
      );
    }
  }

  if (tier.aura) {
    items.push({
      kind: 'aura', frame: frameOf('glow'), atS: 0, x: points[0].x, y: points[0].y, isAdd: true,
      size: tier.aura.sizeFactor * hexSize, alpha: tier.aura.alpha, tint: colors.gold, rotation: 0,
    });
  }
  if (tier.rays) {
    items.push({
      kind: 'auraRays', frame: frameOf('rays'), atS: 0, x: points[0].x, y: points[0].y, isAdd: true,
      size: tier.rays.sizeFactor * hexSize, alpha: tier.rays.alpha, tint: colors.paleGold, rotation: rng() * Math.PI,
    });
  }

  buildBurst({items, burst: tier.departure, center: points[0], atS: 0, hexSize, motifs, isViolent, rng, isArrival: false});
  buildBurst({items, burst: tier.arrival, center: points[points.length - 1], atS: length, hexSize, motifs, isViolent, isEarthshaker, rng, isArrival: true});
  if (isEarthshaker) {
    const arrivalShake = resolveQuakeShake(unitSize, UNIT_FOOTPRINTS.quake.camera.arrivalScale);
    if (arrivalShake) {
      items.push({kind: 'quake', atS: length, ...arrivalShake});
    }
  }

  items.forEach((item) => {
    item.isStay = STAY_KINDS.has(item.kind);
    item.isFollow = FOLLOW_KINDS.has(item.kind);
  });
  // Дальние проступают позже: перебор кадра идет по месту на пути. Сортировка устойчивая:
  // порядок слоев одного отпечатка сохраняется
  items.sort((a, b) => a.atS - b.atS);

  return {points, distances, length, holdMs: tier.holdMs, items};
};

// Деталь отжила свое: убрать ее
const finishFrame = (out) => {
  out.alpha = 0;
  out.isDone = true;
  return out;
};

/**
 * Вид детали следа в данный момент. Считается на каждую деталь в каждом кадре: контроллер
 * передает в out один и тот же объект, и кадр не оставляет мусора. На телефоне копии объектов
 * здесь занимали почти половину времени кадра
 *
 * @param {Object} item
 * @param {number} ageMs — сколько прошло с момента, как деталь проступила
 * @param {number} fadeShare — доля остывания лежащей детали, у света отряда — его угасания
 * @param {Object} [out] — куда записать результат
 * @returns {{x: number, y: number, alpha: number, size: number, rotation: number, isDone: boolean, tint: number}}
 */
export const resolveFootprintItemFrame = (item, ageMs, fadeShare, out = {}) => {
  const {print, flash: flashSettings, spark, dust, ring, crack, ribbon, aura, particles} = UNIT_FOOTPRINTS;
  out.x = item.x;
  out.y = item.y;
  out.rotation = item.rotation;
  out.size = item.size;
  out.alpha = item.alpha;
  out.isDone = false;
  out.tint = item.tint;
  out.scaleX = undefined;
  out.scaleY = undefined;
  const fade = clamp(fadeShare, 0, 1);

  if (item.isFollow) {
    if (fade >= 1) {
      return finishFrame(out);
    }
    const appear = clamp(ageMs / aura.appearMs, 0, 1);
    out.alpha = item.alpha * appear * (1 - fade);
    out.size = item.size * (1 + fade * 0.2);
    out.rotation = item.rotation + (item.kind === 'auraRays' ? aura.turn * (ageMs / 1000) : 0);
    return out;
  }

  if (item.isStay) {
    if (fadeShare >= 1) {
      return finishFrame(out);
    }
    if (item.kind === 'print') {
      const pop = clamp(ageMs / print.popMs, 0, 1);
      const settle = clamp(ageMs / print.settleMs, 0, 1);
      const liveHeat = 1 + (item.restHeat - 1) * settle * (2 - settle);
      const vanish = clamp((fade - print.vanishFromShare) / (1 - print.vanishFromShare), 0, 1);
      out.alpha = item.alpha * pop * (1 - vanish);
      out.size = item.size * (print.popFromScale + (1 - print.popFromScale) * pop);
      out.tint = resolveHeatTint(liveHeat * (1 - fade));
      return out;
    }
    if (item.kind === 'burn') {
      const pop = clamp(ageMs / print.popMs, 0, 1);
      const settle = clamp(ageMs / print.settleMs, 0, 1);
      out.alpha = item.alpha * pop * (1 - fade) ** 1.6;
      out.size = item.size * (1.2 - 0.2 * settle);
      return out;
    }
    if (item.kind === 'scorch') {
      out.alpha = item.alpha * clamp(ageMs / print.popMs, 0, 1) * (1 - fade);
      return out;
    }
    if (item.kind === 'crack') {
      const grow = clamp(ageMs / crack.growMs, 0, 1);
      out.alpha = item.alpha * grow * (1 - fade) ** (item.isAdd ? 2 : 1);
      out.size = item.size * (0.55 + 0.45 * grow);
      return out;
    }
    out.alpha = item.alpha * clamp(ageMs / ribbon.appearMs, 0, 1) * (1 - fade) ** 1.4;
    return out;
  }

  const localMs = ageMs - (item.delayMs ?? 0);
  if (localMs < 0) {
    out.alpha = 0;
    return out;
  }

  if (item.kind === 'flash') {
    const share = localMs / (item.lifeMs ?? flashSettings.lifeMs);
    if (share >= 1) {
      return finishFrame(out);
    }
    const eased = 1 - (1 - share) * (1 - share);
    out.size = item.size * (flashSettings.fromScale + (flashSettings.toScale - flashSettings.fromScale) * eased);
    out.rotation = item.rotation + (item.turn ?? 0) * share;
    out.alpha = item.alpha * (1 - share) ** 1.4 * clamp(share * 10, 0, 1);
    return out;
  }

  if (item.kind === 'spark') {
    const share = localMs / (item.lifeMs ?? spark.lifeMs);
    if (share >= 1) {
      return finishFrame(out);
    }
    const eased = 1 - (1 - share) * (1 - share);
    const seconds = localMs / 1000;
    out.x = item.x + (item.driftX ?? 0) * eased;
    out.y = item.y + (item.driftY ?? 0) * eased - (item.rise ?? 0) * eased + (item.fall ?? 0) * seconds * seconds * 2;
    out.rotation = item.rotation + (item.turn ?? 0) * share;
    out.size = item.size * (1 - share * 0.55);
    out.alpha = item.alpha * clamp((1 - share) * 2.2, 0, 1);
    return out;
  }

  if (THROWN_KINDS.has(item.kind)) {
    const settings = particles[item.kind];
    const share = localMs / (item.lifeMs ?? settings.lifeMs);
    if (share >= 1) {
      return finishFrame(out);
    }
    // Вылетает на полной скорости, падает на землю и больше не летит
    const {landShare, bounce = 0, flips = 0, isAligned = false, stretch = 1} = settings;
    const flight = clamp(share / landShare, 0, 1);
    const travelled = 1 - (1 - flight) ** 2;
    const hop = bounce > 0 ? clamp((share - landShare) / particles.hopShare, 0, 1) : 0;
    const lift = item.lift ?? 0;
    const lifted = lift * 4 * flight * (1 - flight) + lift * bounce * 4 * hop * (1 - hop);
    const path = travelled + particles.hopTravel * hop;
    const restFrom = landShare + (bounce > 0 ? particles.hopShare : 0);
    out.x = item.x + (item.driftX ?? 0) * path;
    out.y = item.y + (item.driftY ?? 0) * path - lifted;
    out.rotation = item.rotation + (item.turn ?? 0) * Math.min(1, path);
    out.alpha = item.alpha * (share < restFrom ? 1 : 1 - (share - restFrom) / (1 - restFrom));
    if (flips > 0) {
      // Вертится ребром, а упав, лежит лицом вверх
      const spinning = Math.min(1, (flight + hop) / 2);
      out.scaleX = hop >= 1 ? 1 : 0.14 + 0.86 * Math.abs(Math.cos(spinning * flips * Math.PI * 2));
    }
    if (isAligned) {
      // Капля смотрит хвостом назад: острый конец рисунка — против полета
      const velocityX = (item.driftX ?? 0) * 2 * (1 - flight);
      const velocityY = (item.driftY ?? 0) * 2 * (1 - flight) - lift * 4 * (1 - 2 * flight);
      out.rotation = Math.atan2(velocityY, velocityX) - Math.PI / 2;
      out.scaleY = stretch;
    }
    return out;
  }

  if (item.kind === 'dust') {
    const share = localMs / dust.lifeMs;
    if (share >= 1) {
      return finishFrame(out);
    }
    out.x = item.x + (item.driftX ?? 0) * share;
    // Низкая пыль стелется по земле, обычная поднимается
    out.y = item.y + (item.driftY ?? 0) * share - (item.isLow ? 0 : dust.riseFactor * item.size * share);
    out.size = item.size * (1 + (dust.growTo - 1) * Math.sqrt(share));
    out.alpha = item.alpha * (1 - share) * clamp(share * 8, 0, 1);
    return out;
  }

  if (item.kind === 'ring') {
    const share = localMs / (item.lifeMs ?? ring.lifeMs);
    if (share >= 1) {
      return finishFrame(out);
    }
    const eased = 1 - (1 - share) * (1 - share);
    out.size = item.size * (ring.fromFactor + (1 - ring.fromFactor) * eased);
    out.alpha = item.alpha * (1 - share) ** 1.3;
    return out;
  }

  const settings = particles[item.kind];
  const share = localMs / (item.lifeMs ?? settings.lifeMs);
  if (share >= 1) {
    return finishFrame(out);
  }

  if (item.kind === 'flame') {
    // Разгорается на месте и с ускорением уходит вверх, сужаясь к концу
    const grow = clamp(share / 0.18, 0, 1);
    out.y = item.y - (item.rise ?? 0) * share ** 1.6;
    out.size = item.size * grow * (1 - 0.6 * share);
    out.alpha = item.alpha * clamp((1 - share) * 2.4, 0, 1);
    return out;
  }

  if (item.kind === 'flutter') {
    // Подлетает невысоко и планирует вниз: качается из стороны в сторону, а не вертится
    const swing = Math.sin((item.swayPhase ?? 0) + share * (item.swayTurns ?? 0) * Math.PI * 2);
    const upShare = clamp(share / 0.26, 0, 1);
    out.x = item.x + (item.driftX ?? 0) * share + (item.sway ?? 0) * swing;
    out.y = item.y - (item.lift ?? 0) * (1 - (1 - upShare) ** 2) + (item.fall ?? 0) * share ** 2;
    out.rotation = item.rotation + (item.rock ?? 0) * swing;
    out.size = item.size * clamp(0.6 + share * 6, 0, 1);
    out.alpha = item.alpha * clamp((1 - share) * 3, 0, 1);
    return out;
  }

  if (item.kind === 'puff') {
    const eased = 1 - (1 - share) ** 2;
    out.x = item.x + (item.driftX ?? 0) * eased;
    out.y = item.y + (item.driftY ?? 0) * eased - (item.rise ?? 0) * eased;
    out.rotation = item.rotation + (item.turn ?? 0) * share;
    out.size = item.size * (1 + (settings.growTo - 1) * eased);
    // Проступает и рассеивается плавно: у дыма нет резкого начала
    out.alpha = item.alpha * clamp(share * 4, 0, 1) * (1 - share) ** 1.3;
    return out;
  }

  // Всплывает прямо вверх, замедляясь, и тает
  const eased = 1 - (1 - share) ** 2;
  out.x = item.x + (item.wobble ?? 0) * Math.sin((item.wobblePhase ?? 0) + share * Math.PI * 2);
  out.y = item.y - (item.rise ?? 0) * eased;
  out.size = item.size * clamp(0.6 + share * 4, 0, 1);
  out.alpha = item.alpha * clamp(share * 8, 0, 1) * clamp((1 - share) * 2, 0, 1);
  return out;
};
